export default function About() {
  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* LEFT: Text content */}
          <div className="space-y-7">
            <div>
              <div className="inline-block px-4 py-1.5 bg-brand-50 border border-brand-100 rounded-full mb-5">
                <span className="text-xs font-semibold text-brand-600 tracking-widest font-heading">ABOUT CSCF</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-5 font-heading">
                An ultra-rare condition.<br />A community that understands.
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-4 font-body">
                Cardiospondylocarpofacial syndrome (CSCF) is an ultra-rare genetic condition caused by variants in the <strong className="text-gray-800">MAP3K7</strong> gene. It affects multiple body systems — the heart, spine, hands, feet, face, and hearing — and can present very differently in each person.
              </p>
              <p className="text-gray-600 text-base leading-relaxed font-body">
                Because fewer than 30 cases have been genetically confirmed worldwide, families often spend years searching for answers before receiving a diagnosis. The rarity makes finding specialists, information, and peer support a tremendous challenge.
              </p>
            </div>

            {/* Rarity callout */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 flex items-start gap-5">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-50 rounded-xl flex-shrink-0">
                <i className="ri-search-eye-line text-brand-500 text-xl"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-1 font-heading">Exceptionally Rare — and Often Missed</p>
                <p className="text-sm text-gray-500 font-body leading-relaxed">
                  Fewer than 30 cases have been genetically confirmed worldwide. Many more may go undiagnosed or misdiagnosed. <strong className="text-gray-700">Whole exome sequencing (WES)</strong> is the only way to confirm CSCF.
                </p>
              </div>
            </div>

            {/* Genetic testing nudge */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl flex-shrink-0">
                <i className="ri-test-tube-line text-emerald-600 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 font-heading">Genetic testing is essential</p>
                <p className="text-xs text-gray-500 font-body leading-relaxed">A confirmed MAP3K7 variant unlocks the right care, the right specialists, and connection to this community.</p>
              </div>
              <a
                href="#genetic-testing"
                className="flex-shrink-0 text-xs font-semibold text-emerald-700 hover:text-emerald-800 transition-colors whitespace-nowrap cursor-pointer font-body flex items-center gap-1"
              >
                Learn more <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Affected systems row */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4 font-heading">Systems Affected</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: 'ri-heart-pulse-line', label: 'Heart', bg: 'bg-red-50', color: 'text-red-500' },
                  { icon: 'ri-body-scan-line', label: 'Bones', bg: 'bg-brand-50', color: 'text-brand-500' },
                  { icon: 'ri-volume-mute-line', label: 'Hearing', bg: 'bg-accent-50', color: 'text-accent-500' },
                  { icon: 'ri-brain-line', label: 'Development', bg: 'bg-emerald-50', color: 'text-emerald-500' },
                ].map((f) => (
                  <div key={f.label} className="flex flex-col items-center bg-white border border-gray-100 rounded-xl p-4 text-center">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full mb-2 ${f.bg}`}>
                      <i className={`${f.icon} text-xl ${f.color}`}></i>
                    </div>
                    <span className="text-xs font-semibold text-gray-700 font-body">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/science"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-brand-500 text-white font-semibold rounded-full hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer font-body"
              >
                <span>View the Research</span>
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a
                href="#resources"
                className="inline-flex items-center justify-center px-7 py-3.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-brand-300 hover:text-brand-600 transition-colors whitespace-nowrap cursor-pointer font-body"
              >
                Find Support Resources
              </a>
            </div>
          </div>

          {/* RIGHT: Image */}
          <div className="relative mt-8 lg:mt-0">
            <div className="rounded-2xl overflow-hidden w-full h-64 sm:h-96 lg:h-[520px]">
              <img
                src="https://storage.readdy-site.link/project_files/c4084cf6-f9fc-491d-816c-98ce35896c67/52e5550b-2c36-4472-901e-a91bc76a5227_shutterstock_1090089269.jpg?v=4831755d6be1d6b2d5423d5422860236"
                alt="Family affected by Cardiospondylocarpofacial syndrome (CSCF) receiving support and care"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Floating card — hidden on small screens to avoid overflow */}
            <div className="hidden sm:block absolute -bottom-5 -left-5 bg-white rounded-2xl p-5 w-52 border border-gray-100">
              <div className="text-3xl font-bold text-brand-500 mb-1 font-heading">&lt;30</div>
              <div className="text-xs text-gray-500 leading-snug font-body">Genetically confirmed cases worldwide as of 2025</div>
            </div>
            {/* Top badge — hidden on small screens */}
            <div className="hidden sm:block absolute -top-4 -right-4 bg-brand-500 text-white rounded-2xl px-4 py-3">
              <p className="text-xs font-semibold font-heading">MAP3K7 Gene</p>
              <p className="text-xs text-white/70 font-body">Chromosome 6q15</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
